/**
 * Cielo utility functions for SOP integration
 */

export const BRAZILIAN_STATES = [
  { value: '', display: '—' },
  { value: 'AC', display: 'Acre' },
  { value: 'AL', display: 'Alagoas' },
  { value: 'AP', display: 'Amapá' },
  { value: 'AM', display: 'Amazonas' },
  { value: 'BA', display: 'Bahia' },
  { value: 'CE', display: 'Ceará' },
  { value: 'DF', display: 'Distrito Federal' },
  { value: 'ES', display: 'Espírito Santo' },
  { value: 'GO', display: 'Goiás' },
  { value: 'MA', display: 'Maranhão' },
  { value: 'MT', display: 'Mato Grosso' },
  { value: 'MS', display: 'Mato Grosso do Sul' },
  { value: 'MG', display: 'Minas Gerais' },
  { value: 'PA', display: 'Pará' },
  { value: 'PB', display: 'Paraíba' },
  { value: 'PR', display: 'Paraná' },
  { value: 'PE', display: 'Pernambuco' },
  { value: 'PI', display: 'Piauí' },
  { value: 'RJ', display: 'Rio de Janeiro' },
  { value: 'RN', display: 'Rio Grande do Norte' },
  { value: 'RS', display: 'Rio Grande do Sul' },
  { value: 'RO', display: 'Rondônia' },
  { value: 'RR', display: 'Roraima' },
  { value: 'SC', display: 'Santa Catarina' },
  { value: 'SP', display: 'São Paulo' },
  { value: 'SE', display: 'Sergipe' },
  { value: 'TO', display: 'Tocantins' },
];

/**
 * Selectable credit card brands for the Cielo flow.
 * Values match the `card.type` strings returned by `card-validator` so a manual
 * selection stays consistent with the auto-detected brand and the backend payload.
 */
export const CARD_BRANDS = [
  { value: '', display: '—' },
  { value: 'visa', display: 'Visa' },
  { value: 'mastercard', display: 'Mastercard' },
  { value: 'elo', display: 'Elo' },
  { value: 'american-express', display: 'American Express' },
  { value: 'hipercard', display: 'Hipercard' },
  { value: 'diners-club', display: 'Diners Club' },
  { value: 'discover', display: 'Discover' },
  { value: 'jcb', display: 'JCB' },
];

/**
 * Compose a billing address string from structured form fields.
 * @param {object} fields - rua, numero, complemento, bairro, cidade, estado, cep.
 * @param {string} fields.rua - Street name.
 * @param {string} fields.numero - Street number.
 * @param {string} fields.complemento - Address complement.
 * @param {string} fields.bairro - Neighborhood.
 * @param {string} fields.cidade - City.
 * @param {string} fields.estado - State abbreviation.
 * @param {string} fields.cep - Postal code.
 * @returns {string} Formatted billing address string.
 */
export const composeBillingAddress = ({
  rua,
  numero,
  complemento,
  bairro,
  cidade,
  estado,
  cep,
}) => {
  const parts = [rua, numero, complemento, bairro, cidade, estado, cep].filter(Boolean);
  return parts.join(', ');
};

/**
 * Builds a structured (camelCase) billing address object from the Cielo form
 * values, suitable for the backend's `address_attributes` payload.
 *
 * Field mapping: rua→street, numero→number, bairro→neighborhood, cidade→city,
 * cep→zipCode, plus a fixed countryCode of 'BR'. The `estado` and `complemento`
 * fields are intentionally NOT forwarded — Cybersource has no destination for
 * them, so they remain collected UI-only fields.
 *
 * Returns `null` when the billing-address section is disabled (the
 * `includeBillingAddress` toggle is off) or when any required field is empty,
 * so that no address is sent.
 *
 * @param {object} fields - The Formik form values.
 * @param {boolean} [fields.includeBillingAddress] - Whether the user opted in.
 * @param {string} [fields.rua] - Street name.
 * @param {string} [fields.numero] - Street number.
 * @param {string} [fields.bairro] - Neighborhood.
 * @param {string} [fields.cidade] - City.
 * @param {string} [fields.cep] - Postal code.
 * @returns {?object} Structured address attributes, or `null` when omitted.
 */
export const buildAddressAttributes = ({
  includeBillingAddress,
  rua,
  numero,
  bairro,
  cidade,
  cep,
} = {}) => {
  if (!includeBillingAddress) {
    return null;
  }

  const trim = (value) => (typeof value === 'string' ? value.trim() : value);
  const street = trim(rua);
  const number = trim(numero);
  const neighborhood = trim(bairro);
  const city = trim(cidade);
  const zipCode = cep == null ? cep : String(cep).replace(/\D/g, '');

  // Require the forwarded fields; if any are missing or the CEP is malformed,
  // send nothing so a bad address never reaches the payload boundary.
  if (!street || !number || !neighborhood || !city || !zipCode || !/^\d{8}$/.test(zipCode)) {
    return null;
  }

  return {
    street,
    number,
    neighborhood,
    city,
    zipCode,
    countryCode: 'BR',
  };
};

/**
 * Gets the correct Cielo Silent Order Post script URL based on environment.
 * @param {string} environment - The environment ('sandbox' or 'production')
 * @returns {string} The correct SOP script URL for the environment
 */
export const getSopScriptUrl = (environment = 'sandbox') => {
  const urls = {
    sandbox: 'https://transactionsandbox.pagador.com.br/post/scripts/silentorderpost-1.0.min.js',
    production:
      'https://transaction.cieloecommerce.cielo.com.br/post/scripts/silentorderpost-1.0.min.js',
  };

  return urls[environment] || urls.sandbox;
};

/**
 * Validates if a Cielo configuration has required fields for the selected flow.
 * @param {object} config - The Cielo configuration object
 * @returns {object} Validation result with isValid and errors
 */
export const validateCieloConfig = (config) => {
  const errors = [];

  if (!config.enabled) {
    return { isValid: true, errors };
  }

  // Check that at least one flow is enabled
  if (!config.enableThreeDS && !config.enableSilentOrderPost) {
    errors.push('At least one flow (3DS or SOP) must be enabled when Cielo is enabled');
  }

  // Validate 3DS specific fields
  if (config.enableThreeDS) {
    if (!config.threeDSScriptSrc) {
      errors.push('threeDSScriptSrc is required when 3DS is enabled');
    }
    if (!config.environment) {
      errors.push('environment is required when 3DS is enabled');
    }
  }

  // Validate SOP specific fields
  if (config.enableSilentOrderPost) {
    if (!config.sopAccessTokenEndpoint) {
      errors.push('sopAccessTokenEndpoint is required when SOP is enabled');
    }
    if (!config.sopEnvironment) {
      errors.push('sopEnvironment is required when SOP is enabled');
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};
